import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Badge, Box, Button, HStack, Heading, Spinner, Switch, Text } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiRefreshCw, FiTrash2, FiUpload } from 'react-icons/fi'
import { useDeleteLegislator, useLegislators, useSyncLegislators } from '../hooks/useLobbyDay'
import { useAvailableStates } from '../hooks/useAvailableStates'
import { useJobPolling } from '../hooks/useJobPolling'
import { LegislatorFormModal } from '../components/lobbyDay/LegislatorFormModal'
import { LegislatorImportModal } from '../components/lobbyDay/LegislatorImportModal'
import { LegiScanCredit } from '../components/lobbyDay/LegiScanCredit'
import { LegislatorResourceLinks } from '../components/lobbyDay/LegislatorResourceLinks'
import { SyncFromLegiScanModal, type SyncFromLegiScanRequest } from '../components/lobbyDay/SyncFromLegiScanModal'
import { ResponsiveTable, FilterBar, type ResponsiveColumn } from '../components/shared'
import { CHAMBER_FILTER_ANY, CHAMBER_LABELS, chamberFilterParams } from '../components/lobbyDay/chamberFilter'
import { ChamberFilterSelect } from '../components/lobbyDay/ChamberFilterSelect'
import type { LegislatorDto } from '../types/lobbyDay'

interface LegislatorSyncResult {
  added: number
  updated: number
  adopted: number
  deactivated: number
  unchanged: number
}

export function LegislatorsPage() {
  const qc = useQueryClient()
  const [chamberFilter, setChamberFilter] = useState(CHAMBER_FILTER_ANY)
  const [search, setSearch] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [isFormOpen, setFormOpen] = useState(false)
  const [isImportOpen, setImportOpen] = useState(false)
  const [isSyncOpen, setSyncOpen] = useState(false)
  const [selected, setSelected] = useState<LegislatorDto | undefined>(undefined)
  const [syncJobId, setSyncJobId] = useState<string | null>(null)

  const { chamber, level } = chamberFilterParams(chamberFilter)

  const { data: legislators, isLoading, error } = useLegislators({
    chamber,
    level,
    search: search || undefined,
    includeInactive,
  })
  const deleteMutation = useDeleteLegislator()
  const syncMutation = useSyncLegislators()
  const { data: availableStates } = useAvailableStates()
  const syncPoll = useJobPolling(syncJobId)
  const isSyncing = !!syncJobId && !syncPoll.isTerminal

  useEffect(() => {
    if (!syncJobId || !syncPoll.isTerminal) return

    const succeeded = syncPoll.data?.status === 'Succeeded'
    const result = succeeded ? (syncPoll.data?.result as LegislatorSyncResult | undefined) : undefined
    const errorMessage = syncPoll.data?.errorMessage ?? undefined

    // TV-FLUSHSYNC: defer the toast out of the effect's commit phase. Chakra's
    // toaster.create portal-renders synchronously, which triggers a
    // flushSync-was-called-from-inside-a-lifecycle-method warning when invoked
    // during commit. A microtask lets commit finish first.
    queueMicrotask(() => {
      if (succeeded) {
        toaster.create({
          type: 'success',
          title: 'Synced from LegiScan',
          description: result
            ? `${result.added} added, ${result.updated} updated, ${result.adopted} adopted, ${result.deactivated} deactivated`
            : undefined,
        })
        qc.invalidateQueries({ queryKey: ['legislators'] })
      } else {
        toaster.create({
          type: 'error',
          title: 'Sync failed',
          description: errorMessage,
        })
      }
    })
    setSyncJobId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncJobId, syncPoll.isTerminal, syncPoll.data])

  const submitSync = async (req: SyncFromLegiScanRequest) => {
    // Guard against a rapid double-click on the modal's Sync button firing two enqueue
    // requests: mutateAsync is async, so the modal stays open (and its button enabled) for
    // the round trip. A second onSubmit while the first is still in flight is a no-op.
    if (syncMutation.isPending) return
    try {
      const { jobId } = await syncMutation.mutateAsync(req)
      setSyncJobId(jobId)
      setSyncOpen(false)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to start sync'
      toaster.create({ type: 'error', title: 'Error', description: message })
    }
  }

  const openCreate = () => { setSelected(undefined); setFormOpen(true) }
  const openEdit = (legislator: LegislatorDto) => { setSelected(legislator); setFormOpen(true) }
  const closeForm = () => { setFormOpen(false); setSelected(undefined) }

  const openDelete = async (legislator: LegislatorDto) => {
    try {
      await deleteMutation.mutateAsync(legislator.id)
      toaster.create({ type: 'success', title: 'Legislator deleted' })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to delete legislator'
      toaster.create({ type: 'error', title: 'Error', description: message })
    }
  }

  const columns: ResponsiveColumn<LegislatorDto>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (leg) => leg.fullName,
    },
    {
      header: 'Chamber',
      mobileRole: 'badge',
      cell: (leg) => CHAMBER_LABELS[leg.chamber],
    },
    {
      header: 'District',
      mobileRole: 'meta',
      cell: (leg) => leg.district,
    },
    {
      header: 'Party',
      mobileRole: 'badge',
      cell: (leg) => leg.party ?? '—',
    },
    {
      header: 'Office',
      mobileRole: 'meta',
      cell: (leg) => leg.officeRoom ?? '—',
    },
    {
      header: 'Meetings',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (leg) => leg.meetingCount,
    },
    {
      header: 'Source',
      mobileRole: 'badge',
      cell: (leg) => (
        <Badge colorPalette={leg.source === 'LegiScan' ? 'blue' : 'gray'}>{leg.source}</Badge>
      ),
    },
    {
      header: 'Links',
      mobileRole: 'desktop-only',
      cell: (leg) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <LegislatorResourceLinks
            variant="inline"
            ballotpediaUrl={leg.ballotpediaUrl}
            voteSmartUrl={leg.voteSmartUrl}
            openSecretsUrl={leg.openSecretsUrl}
            followTheMoneyUrl={leg.followTheMoneyUrl}
          />
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <HStack justify="space-between" mb={6} flexWrap="wrap" gap={2}>
        <Heading size="lg">Legislators</Heading>
        <HStack>
          <Button size="sm" onClick={() => setSyncOpen(true)} loading={isSyncing} loadingText="Syncing…">
            <FiRefreshCw />Sync from LegiScan
          </Button>
          <Button size="sm" onClick={() => setImportOpen(true)}><FiUpload />Paste import
                      </Button>
          <Button colorPalette="brand" size="sm" onClick={openCreate}><FiPlus />Add legislator
                      </Button>
        </HStack>
      </HStack>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search name…' }}
        filterCount={chamberFilter ? 1 : 0}
      >
        <ChamberFilterSelect value={chamberFilter} onChange={setChamberFilter} />
        <Switch.Root
          size="sm"
          checked={includeInactive}
          onCheckedChange={(e) => setIncludeInactive(e.checked === true)}
        >
          <Switch.HiddenInput />
          <Switch.Control><Switch.Thumb /></Switch.Control>
          <Switch.Label>
            <Text fontSize="sm" whiteSpace="nowrap">Show inactive</Text>
          </Switch.Label>
        </Switch.Root>
      </FilterBar>

      {isSyncing && (
        <HStack mb={3} gap={2}>
          <Spinner size="sm" color="accent" />

          <Text fontSize="sm">Syncing legislators from LegiScan…</Text>
        </HStack>
      )}

      {!!error && (
        <Text color="red.500">Failed to load legislators.</Text>
      )}

      {!error && (
        <ResponsiveTable
          columns={columns}
          rows={legislators ?? []}
          keyFn={(leg) => leg.id}
          isLoading={isLoading}
          onRowClick={openEdit}
          rowActions={[{
            label: 'Delete',
            icon: FiTrash2,
            color: 'red.500',
            onClick: openDelete,
            isLoading: (leg) => deleteMutation.isPending && deleteMutation.variables === leg.id,
          }]}
          emptyMessage="No legislators yet. Add one or paste from a spreadsheet."
        />
      )}

      <Box mt={3}>
        <LegiScanCredit />
      </Box>

      <LegislatorFormModal isOpen={isFormOpen} onClose={closeForm} legislator={selected} />
      <LegislatorImportModal isOpen={isImportOpen} onClose={() => setImportOpen(false)} />
      <SyncFromLegiScanModal isOpen={isSyncOpen} onClose={() => setSyncOpen(false)} onSubmit={submitSync} states={availableStates ?? []} />
    </Box>
  );
}
